// Sorte de feuille « entiers » (lots I7 et I13) : congruences (plugin `congruence`) et arithmétique (plugin
// `arith` : PGCD, PPCM, divisions euclidiennes, Bézout). Calcul EXACT sur les entiers (BigInt : 3²⁰²⁶ se calcule).
// Les gestes et leurs libellés viennent du pack `arith.entiers`.

import { isLeaf } from './ast.js';

/** « … [7] » → { text sans le module, mod: 7n } ; sinon { text, mod: null } */
export function splitModulus(text) {
  const m = /\[\s*(\d+)\s*\]\s*$/u.exec(String(text));
  return m
    ? { text: String(text).slice(0, m.index).trim(), mod: BigInt(m[1]) }
    : { text: String(text), mod: null };
}

/** Chaîne « a = b ≡ c » (hors parenthèses) → { members: [...], ops: ['=', '≡'] } */
export function splitChain(text) {
  const members = [],
    ops = [];
  let depth = 0,
    start = 0;
  const s = String(text);
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '(' || c === '[') depth++;
    else if (c === ')' || c === ']') depth--;
    else if (depth === 0 && (c === '=' || c === '≡')) {
      members.push(s.slice(start, i).trim());
      ops.push(c);
      start = i + 1;
    }
  }
  members.push(s.slice(start).trim());
  return { members, ops };
}

/** Affectations « u = 2 ; v = −1 » → [['u', '2'], ['v', '−1']] ou null */
export function splitAssignments(text) {
  const parts = String(text)
    .split(';')
    .map((x) => x.trim());
  if (parts.length < 2) return null;
  const out = [];
  for (const p of parts) {
    const m = /^([A-Za-z])\s*=\s*(.+)$/u.exec(p);
    if (!m) return null;
    out.push([m[1], m[2]]);
  }
  return out;
}

const babs = (x) => (x < 0n ? -x : x);
export const bgcd = (a, b) => {
  a = babs(a);
  b = babs(b);
  while (b) [a, b] = [b, a % b];
  return a;
};
export const mod = (a, n) => ((a % n) + n) % n;

/**
 * Valeur entière exacte d'une expression (BigInt), ou null (lettre inconnue, division non exacte, exposant négatif…).
 * env : lettre → BigInt.
 */
export function intValue(n, table, env = {}) {
  if (n.t === 'Num') return n.q === 1 ? BigInt(n.p) : null;
  if (n.t === 'Sym') return Object.prototype.hasOwnProperty.call(env, n.name) ? env[n.name] : null;
  if (isLeaf(n)) return null;
  const ev = table.nodes[n.t]?.eval;
  if (ev === 'call') {
    const f = table.syntax.functions[n.name]?.eval;
    const a = n.k.map((c) => intValue(c, table, env));
    if (a.some((x) => x === null)) return null;
    if (f === 'gcd' && a.length === 2) return bgcd(a[0], a[1]);
    if (f === 'lcm' && a.length === 2)
      return a[0] && a[1] ? babs(a[0] * a[1]) / bgcd(a[0], a[1]) : null;
    return null;
  }
  if (ev === 'fact') {
    const a = intValue(n.k[0], table, env);
    if (a === null || a < 0n || a > 300n) return null;
    let p = 1n;
    for (let k = 2n; k <= a; k++) p *= k;
    return p;
  }
  const ks = n.k.map((c) => intValue(c, table, env));
  if (ks.some((x) => x === null)) return null;
  if (ev === 'add') return ks.reduce((s, x) => s + x, 0n);
  if (ev === 'neg') return -ks[0];
  if (ev === 'mul') return ks.reduce((s, x) => s * x, 1n);
  if (ev === 'div') return ks[1] !== 0n && ks[0] % ks[1] === 0n ? ks[0] / ks[1] : null;
  if (ev === 'pow') return ks[1] >= 0n && ks[1] <= 100000n ? ks[0] ** ks[1] : null;
  return null;
}

/** Valeur modulo n (puissances par exponentiation rapide : jamais de nombre géant). */
export function modValue(n, table, m, env = {}) {
  if (n.t === 'Num') return n.q === 1 ? mod(BigInt(n.p), m) : null;
  if (n.t === 'Sym')
    return Object.prototype.hasOwnProperty.call(env, n.name) ? mod(env[n.name], m) : null;
  if (isLeaf(n)) return null;
  const ev = table.nodes[n.t]?.eval;
  if (ev === 'pow') {
    const b = modValue(n.k[0], table, m, env),
      e = intValue(n.k[1], table, env);
    if (b === null || e === null || e < 0n) return null;
    let r = 1n,
      x = b,
      k = e;
    while (k > 0n) {
      if (k & 1n) r = (r * x) % m;
      x = (x * x) % m;
      k >>= 1n;
    }
    return r;
  }
  const ks = n.k.map((c) => modValue(c, table, m, env));
  if (ks.some((x) => x === null)) return null;
  if (ev === 'add')
    return mod(
      ks.reduce((s, x) => s + x, 0n),
      m,
    );
  if (ev === 'neg') return mod(-ks[0], m);
  if (ev === 'mul')
    return mod(
      ks.reduce((s, x) => s * x, 1n),
      m,
    );
  const v = intValue(n, table, env);
  return v === null ? null : mod(v, m);
}

export const isPrimeBig = (p) => {
  if (p < 2n) return false;
  for (let d = 2n; d * d <= p; d++) if (p % d === 0n) return false;
  return true;
};

/** Écriture « produit de nombres premiers (puissances permises) » */
export function isPrimeProduct(n, table) {
  const ev = table.nodes[n.t]?.eval;
  const fs =
    ev === 'mul' ? n.k.flatMap((c) => (table.nodes[c.t]?.eval === 'mul' ? c.k : [c])) : [n];
  return (
    fs.every((f) => {
      if (f.t === 'Num') return f.q === 1 && isPrimeBig(BigInt(f.p));
      if (table.nodes[f.t]?.eval === 'pow')
        return f.k[0].t === 'Num' && isPrimeBig(BigInt(f.k[0].p)) && f.k[1].t === 'Num';
      return false;
    }) && fs.length > 0
  );
}

/** « a = b × q + r » avec 0 ≤ r < b (ou q × b + r) */
export function isEuclid(lhs, rhs, table) {
  if (table.nodes[rhs.t]?.eval !== 'add' || rhs.k.length !== 2) return false;
  const [p, r] = table.nodes[rhs.k[0].t]?.eval === 'mul' ? rhs.k : [rhs.k[1], rhs.k[0]];
  if (table.nodes[p.t]?.eval !== 'mul' || p.k.length !== 2) return false;
  const rv = intValue(r, table),
    a = intValue(lhs, table),
    f = p.k.map((c) => intValue(c, table));
  if (rv === null || a === null || f.some((x) => x === null)) return false;
  return f.some((b) => rv >= 0n && rv < b);
}
